import React, { useState, useEffect } from 'react';
import AdminMenuManager from './AdminMenuManager';
import AffiliateMenuManager from './AffiliateMenuManager';
import AffiliateManagement from './AffiliateManagement';
import MealPlanStats from './MealPlanStats';
import NutritionPlanStats from './NutritionPlanStats';
import FitnessAdminDashboard from './FitnessAdminDashboard';
import WebsiteStats from './WebsiteStats';
import AIMetricsDashboard from './AIMetricsDashboard';
import AdminAgents from './AdminAgents';
import AdminWeeklyEmails from './AdminWeeklyEmails';
import { toast } from 'react-hot-toast';
import './AdminDashboard.css';

const TABS = [
  { key: 'websiteStats',      label: 'Website Stats' },
  { key: 'aiMetrics',         label: 'AI Metrics' },
  { key: 'menu',              label: 'Menu Management' },
  { key: 'mealPlanStats',     label: 'Meal Plan Stats' },
  { key: 'nutritionStats',    label: 'Nutrition Stats' },
  { key: 'affiliatePayments', label: 'Affiliate Payments' },
  { key: 'affiliateMenus',    label: 'Affiliate Menus' },
  { key: 'affiliateApps',     label: 'Affiliate Applications' },
  { key: 'fitness',           label: 'Fitness' },
  { key: 'aiAgents',          label: 'AI Agents' },
  { key: 'weeklyEmails',      label: 'Weekly Emails' },
];

const ACTIVE_TAB_KEY = 'adminDashboard.activeTab';

function readStoredActiveTab() {
  try {
    const stored = sessionStorage.getItem(ACTIVE_TAB_KEY);
    return TABS.some((tab) => tab.key === stored) ? stored : 'menu';
  } catch {
    return 'menu';
  }
}

const AffiliatePaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [unpaidBalance, setUnpaidBalance] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchPaymentsData();
  }, [filterStatus, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [paymentsRes, affiliatesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/affiliate-payments?${new URLSearchParams({
          status: filterStatus,
          startDate: dateRange.start,
          endDate: dateRange.end
        })}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/affiliates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!paymentsRes.ok || !affiliatesRes.ok) throw new Error('Failed to fetch data');

      const [paymentsData, affiliatesData] = await Promise.all([
        paymentsRes.json(),
        affiliatesRes.json()
      ]);

      setPayments(paymentsData);
      setAffiliates(affiliatesData);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      toast.error('Failed to fetch payment data');
    }
  };

  const fetchUnpaidBalance = async (affiliateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/affiliates/${affiliateId}/unpaid-balance`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch unpaid balance');
      const data = await response.json();
      setUnpaidBalance(data.unpaidBalance);
      setPaymentAmount(data.unpaidBalance.toFixed(2));
    } catch (error) {
      console.error('Error fetching unpaid balance:', error);
      toast.error('Failed to fetch unpaid balance');
    }
  };

  const handleAffiliateSelect = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    await fetchUnpaidBalance(affiliate._id);
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!selectedAffiliate || !paymentAmount || !periodStart || !periodEnd) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/affiliate-payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          affiliateId: selectedAffiliate._id,
          amount: parseFloat(paymentAmount),
          periodStart,
          periodEnd,
          paypalPayerEmail: selectedAffiliate.paymentInfo.email
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create payment');
      toast.success('Payment processed successfully');
      fetchPaymentsData();
      setSelectedAffiliate(null);
      setPaymentAmount('');
      setPeriodStart('');
      setPeriodEnd('');
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.message);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <div className="admin-payments-tab">
      <div className="payments-grid">
        <div className="affiliates-list">
          <h3>Select Affiliate</h3>
          <div className="affiliates-grid">
            {affiliates.map(affiliate => (
              <div
                key={affiliate._id}
                className={`affiliate-card ${selectedAffiliate?._id === affiliate._id ? 'selected' : ''}`}
                onClick={() => handleAffiliateSelect(affiliate)}
              >
                <h4>{affiliate.businessName}</h4>
                <p>{affiliate.userEmail}</p>
                <p className={`status ${affiliate.status.toLowerCase()}`}>
                  {affiliate.status}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="payment-form">
          <h3>Create Payment</h3>
          {selectedAffiliate ? (
            <form onSubmit={handleCreatePayment}>
              <div className="form-group">
                <label>Affiliate</label>
                <input type="text" value={selectedAffiliate.businessName} disabled />
              </div>
              <div className="form-group">
                <label>PayPal Email</label>
                <input type="email" value={selectedAffiliate.paymentInfo.email} disabled />
              </div>
              <div className="form-group">
                <label>Unpaid Balance</label>
                <input type="text" value={formatCurrency(unpaidBalance)} disabled />
              </div>
              <div className="form-group">
                <label>Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Period Start</label>
                <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Period End</label>
                <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required />
              </div>
              <button type="submit" className="submit-button">Process Payment</button>
            </form>
          ) : (
            <p className="select-affiliate-prompt">Please select an affiliate to process payment</p>
          )}
        </div>

        <div className="payments-list">
          <h3>Payment History</h3>
          <div className="filters">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </select>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              placeholder="Start Date"
            />
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              placeholder="End Date"
            />
          </div>
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Affiliate</th>
                <th>Amount</th>
                <th>Period</th>
                <th>Status</th>
                <th>PayPal Transaction</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment._id}>
                  <td>{formatDate(payment.createdAt)}</td>
                  <td>{payment.affiliateId.businessName}</td>
                  <td>{formatCurrency(payment.amount)}</td>
                  <td>{formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}</td>
                  <td>
                    <span className={`status-badge ${payment.status.toLowerCase()}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td>
                    {payment.paypalTransactionId ? (
                      <a
                        href={`https://www.paypal.com/activity/payment/${payment.paypalTransactionId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Transaction
                      </a>
                    ) : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(readStoredActiveTab);

  useEffect(() => {
    try {
      sessionStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch {
      // sessionStorage may be unavailable
    }
  }, [activeTab]);

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-header">
        <h1>Admin Dashboard</h1>
      </div>

      <nav className="admin-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`admin-tab ${activeTab === tab.key ? 'admin-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="admin-tab-content">
        {activeTab === 'websiteStats' && <WebsiteStats />}
        {activeTab === 'aiMetrics' && <AIMetricsDashboard />}
        {activeTab === 'menu' && <AdminMenuManager />}
        {activeTab === 'mealPlanStats' && <MealPlanStats />}
        {activeTab === 'nutritionStats' && <NutritionPlanStats />}
        {activeTab === 'affiliatePayments' && <AffiliatePaymentsTab />}
        {activeTab === 'affiliateMenus' && <AffiliateMenuManager />}
        {activeTab === 'affiliateApps' && <AffiliateManagement />}
        {activeTab === 'fitness' && <FitnessAdminDashboard embedded />}
        {activeTab === 'aiAgents' && <AdminAgents />}
        {activeTab === 'weeklyEmails' && <AdminWeeklyEmails />}
      </div>
    </div>
  );
};

export default AdminDashboard;
